/**
 * Company Details Page (Dashboard Sub-page)
 * Page for managing company master data with tabs for company info and payment settings
 */

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Save, X } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEffect } from "react";
import { PermissionGuard } from "@/components/auth/permission-guard";
import { PERMISSIONS } from "@/constants/permissions.constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useCompanyDetails, useUpdateCompanyDetails } from "@/features/company-details/hooks/use-company-details";
import type { UpdateCompanyDetails } from "@/features/company-details/types/company-details.types";
import { useTranslation } from "react-i18next";
import { useDocumentTitle } from "@/hooks/use-document-title";

export const Route = createFileRoute("/(app)/dashboard/company-details/")({
  component: CompanyDetailsPage,
});

function CompanyDetailsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  useDocumentTitle('companyDetails.title');
  const { data: companyDetails, isLoading, error } = useCompanyDetails();
  const updateDetails = useUpdateCompanyDetails();

  const schema = z.object({
    companyName: z.string().min(1, t('companyDetails.companyInfo.companyNameRequired')),
    email: z.string().email(t('companyDetails.companyInfo.emailInvalid')),
    court: z.string().optional(),
    registerNumber: z.string().optional(),
    country: z.string().min(1),
    street: z.string().optional(),
    postalCode: z.string().optional(),
    city: z.string().optional(),
    website: z.string().optional(),
    contactFirstName: z.string().optional(),
    contactLastName: z.string().optional(),
    contactEmail: z.string().email().optional().or(z.literal("")),
    contactPhone: z.string().optional(),
    taxNumber: z.string().optional(),
    vatId: z.string().optional(),
    defaultVatRate: z.number().min(0).max(100),
    smallBusinessText: z.string().min(1, t('companyDetails.taxSettings.smallBusinessTextRequired')),
    iban: z.string().optional(),
    bic: z.string().optional(),
    bankName: z.string().optional(),
    pdfShowDebugGrid: z.boolean(),
  });

  const { register, handleSubmit, formState: { errors }, watch, setValue, reset } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      companyName: "",
      email: "",
      court: "",
      registerNumber: "",
      country: "Deutschland",
      street: "",
      postalCode: "",
      city: "",
      website: "",
      contactFirstName: "",
      contactLastName: "",
      contactEmail: "",
      contactPhone: "",
      taxNumber: "",
      vatId: "",
      defaultVatRate: 19,
      smallBusinessText: t('companyDetails.taxSettings.smallBusinessTextPlaceholder'),
      iban: "",
      bic: "",
      bankName: "",
      pdfShowDebugGrid: false,
    },
  });

  // Update form values when companyDetails are loaded
  useEffect(() => {
    if (companyDetails) {
      reset({
        companyName: companyDetails.companyName || "",
        email: companyDetails.email || "",
        court: companyDetails.court || "",
        registerNumber: companyDetails.registerNumber || "",
        country: companyDetails.country || "Deutschland",
        street: companyDetails.street || "",
        postalCode: companyDetails.postalCode || "",
        city: companyDetails.city || "",
        website: companyDetails.website || "",
        contactFirstName: companyDetails.contactFirstName || "",
        contactLastName: companyDetails.contactLastName || "",
        contactEmail: companyDetails.contactEmail || "",
        contactPhone: companyDetails.contactPhone || "",
        taxNumber: companyDetails.taxNumber || "",
        vatId: companyDetails.vatId || "",
        defaultVatRate: Number(companyDetails.defaultVatRate) || 19,
        smallBusinessText: companyDetails.smallBusinessText || t('companyDetails.taxSettings.smallBusinessTextPlaceholder'),
        iban: companyDetails.iban || "",
        bic: companyDetails.bic || "",
        bankName: companyDetails.bankName || "",
        pdfShowDebugGrid: companyDetails.pdfShowDebugGrid ?? false,
      });
    }
  }, [companyDetails, reset, t]);

  const onSubmit = async (data: UpdateCompanyDetails) => {
    try {
      await updateDetails.mutateAsync(data);
      toast({
        title: t('companyDetails.toast.savedTitle'),
        description: t('companyDetails.toast.companyDataSaved'),
      });
      navigate({ to: "/dashboard" });
    } catch {
      toast({
        title: t('companyDetails.toast.errorTitle'),
        description: t('companyDetails.toast.errorMessage'),
        variant: "destructive",
      });
    }
  };

  const handleSave = () => {
    const form = document.querySelector("form") as HTMLFormElement;
    form?.requestSubmit();
  };

  const handleCancel = () => {
    navigate({ to: "/dashboard" });
  };

  if (isLoading) {
    return (
      <div className="w-full max-w-full space-y-4 md:space-y-6 overflow-hidden">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-muted-foreground">{t('companyDetails.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!companyDetails) {
    return (
      <div className="w-full max-w-full space-y-4 md:space-y-6 overflow-hidden">
        <div className="text-center">
          <p className="text-red-500 font-semibold mb-2">{t('companyDetails.error')}</p>
          {error && (
            <p className="text-sm text-muted-foreground">
              {error instanceof Error ? error.message : t('common.error')}
            </p>
          )}
          <p className="text-sm text-muted-foreground mt-4">
            {t('companyDetails.errorDetails')}
          </p>
        </div>
      </div>
    );
  }

  const pdfShowDebugGrid = watch("pdfShowDebugGrid");

  return (
    <div className="w-full max-w-full space-y-4 md:space-y-6 overflow-hidden">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">{t('companyDetails.title')}</h1>
          <p className="text-sm text-muted-foreground md:text-base">
            {t('companyDetails.description')}
          </p>
        </div>
        <div className="flex gap-2">
          <PermissionGuard permission={PERMISSIONS.COMPANY_EDIT}>
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={updateDetails.isPending}
              className="flex-1 sm:flex-initial"
            >
              <X className="h-4 w-4 mr-2" />
              {t('companyDetails.buttons.cancel')}
            </Button>
          </PermissionGuard>
          <PermissionGuard permission={PERMISSIONS.COMPANY_EDIT}>
            <Button
              onClick={handleSave}
              disabled={updateDetails.isPending}
              className="flex-1 sm:flex-initial"
            >
              <Save className="h-4 w-4 mr-2" />
              {updateDetails.isPending ? t('companyDetails.buttons.saving') : t('companyDetails.buttons.save')}
            </Button>
          </PermissionGuard>
        </div>
      </div>

      {/* Single Form with Tabs */}
      <form onSubmit={handleSubmit(onSubmit)} className="w-full max-w-full">
        <Tabs defaultValue="company" className="w-full">
          <TabsList className="grid w-full grid-cols-1 sm:grid-cols-4 sm:max-w-2xl h-auto">
            <TabsTrigger value="company" className="text-sm h-10">{t('companyDetails.tabs.company')}</TabsTrigger>
            <TabsTrigger value="tax" className="text-sm h-10">{t('companyDetails.tabs.tax')}</TabsTrigger>
            <TabsTrigger value="payment" className="text-sm h-10">{t('companyDetails.tabs.payment')}</TabsTrigger>
            <TabsTrigger value="pdf" className="text-sm h-10">{t('companyDetails.tabs.pdf')}</TabsTrigger>
          </TabsList>
          
          {/* Company Info Tab */}
          <TabsContent value="company" className="mt-6">
            <div className="space-y-6 w-full max-w-2xl">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="col-span-1 sm:col-span-2">
                  <Label htmlFor="companyName">{t('companyDetails.companyInfo.companyName')} {t('companyDetails.companyInfo.required')}</Label>
                  <Input id="companyName" {...register("companyName")} />
                  {errors.companyName && <p className="text-sm text-red-500">{String(errors.companyName.message)}</p>}
                </div>

                <div className="col-span-1 sm:col-span-2">
                  <Label htmlFor="email">{t('companyDetails.companyInfo.email')} {t('companyDetails.companyInfo.required')}</Label>
                  <Input id="email" type="email" {...register("email")} />
                  {errors.email && <p className="text-sm text-red-500">{String(errors.email.message)}</p>}
                </div>

                <div className="col-span-1">
                  <Label htmlFor="court">{t('companyDetails.companyInfo.court')}</Label>
                  <Input id="court" {...register("court")} />
                </div>

                <div className="col-span-1">
                  <Label htmlFor="registerNumber">{t('companyDetails.companyInfo.registerNumber')}</Label>
                  <Input id="registerNumber" {...register("registerNumber")} />
                </div>

                <div className="col-span-1 sm:col-span-2">
                  <Label htmlFor="country">{t('companyDetails.companyInfo.country')} {t('companyDetails.companyInfo.required')}</Label>
                  <Input id="country" {...register("country")} />
                </div>

                <div className="col-span-1 sm:col-span-2">
                  <Label htmlFor="street">{t('companyDetails.companyInfo.street')}</Label>
                  <Input id="street" {...register("street")} />
                </div>

                <div className="col-span-1">
                  <Label htmlFor="postalCode">{t('companyDetails.companyInfo.postalCode')}</Label>
                  <Input id="postalCode" {...register("postalCode")} />
                </div>

                <div className="col-span-1">
                  <Label htmlFor="city">{t('companyDetails.companyInfo.city')}</Label>
                  <Input id="city" {...register("city")} />
                </div>

                <div className="col-span-1 sm:col-span-2">
                  <Label htmlFor="website">{t('companyDetails.companyInfo.website')}</Label>
                  <Input id="website" type="url" {...register("website")} />
                </div>

                <div className="col-span-1 sm:col-span-2 pt-4 border-t">
                  <h3 className="font-semibold mb-4">{t('companyDetails.companyInfo.contactPerson')}</h3>
                </div>

                <div className="col-span-1">
                  <Label htmlFor="contactFirstName">{t('companyDetails.companyInfo.contactFirstName')}</Label>
                  <Input id="contactFirstName" {...register("contactFirstName")} />
                </div>

                <div className="col-span-1">
                  <Label htmlFor="contactLastName">{t('companyDetails.companyInfo.contactLastName')}</Label>
                  <Input id="contactLastName" {...register("contactLastName")} />
                </div>

                <div className="col-span-1">
                  <Label htmlFor="contactEmail">{t('companyDetails.companyInfo.contactEmail')}</Label>
                  <Input id="contactEmail" type="email" {...register("contactEmail")} />
                </div>

                <div className="col-span-1">
                  <Label htmlFor="contactPhone">{t('companyDetails.companyInfo.contactPhone')}</Label>
                  <Input id="contactPhone" {...register("contactPhone")} />
                </div>
              </div>
            </div>
          </TabsContent>
          
          {/* Tax Settings Tab */}
          <TabsContent value="tax" className="mt-6">
            <div className="w-full max-w-2xl space-y-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="col-span-1">
                    <Label htmlFor="taxNumber">{t('companyDetails.taxSettings.taxNumber')}</Label>
                    <Input 
                      id="taxNumber" 
                      {...register("taxNumber")} 
                      placeholder={t('companyDetails.taxSettings.taxNumberPlaceholder')}
                    />
                    {errors.taxNumber && (
                      <p className="text-sm text-red-500 mt-1">{String(errors.taxNumber.message)}</p>
                    )}
                  </div>

                  <div className="col-span-1">
                    <Label htmlFor="vatId">{t('companyDetails.taxSettings.vatId')}</Label>
                    <Input 
                      id="vatId" 
                      {...register("vatId")} 
                      placeholder={t('companyDetails.taxSettings.vatIdPlaceholder')}
                    />
                    {errors.vatId && (
                      <p className="text-sm text-red-500 mt-1">{String(errors.vatId.message)}</p>
                    )}
                  </div>
                </div>

                <div className="col-span-1 sm:col-span-2">
                  <Label htmlFor="defaultVatRate">{t('companyDetails.taxSettings.defaultVatRate')}</Label>
                  <Input 
                    id="defaultVatRate" 
                    type="number" 
                    step="0.01"
                    {...register("defaultVatRate", { valueAsNumber: true })} 
                    placeholder={t('companyDetails.taxSettings.defaultVatRatePlaceholder')} 
                  />
                  {errors.defaultVatRate && (
                    <p className="text-sm text-red-500 mt-1">{String(errors.defaultVatRate.message)}</p>
                  )}
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('companyDetails.taxSettings.defaultVatRateHelp')}
                  </p>
                </div>

                <div>
                  <Label htmlFor="smallBusinessText">
                    {t('companyDetails.taxSettings.smallBusinessText')}
                  </Label>
                  <Textarea 
                    id="smallBusinessText" 
                    {...register("smallBusinessText")} 
                    placeholder={t('companyDetails.taxSettings.smallBusinessTextPlaceholder')}
                    rows={3}
                    className="resize-none"
                  />
                  {errors.smallBusinessText && (
                    <p className="text-sm text-red-500 mt-1">{String(errors.smallBusinessText.message)}</p>
                  )}
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('companyDetails.taxSettings.smallBusinessTextHelp')}
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>
          
          {/* Payment Settings Tab */}
          <TabsContent value="payment" className="mt-6">
            <div className="w-full max-w-2xl space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="col-span-1 sm:col-span-2">
                  <Label htmlFor="iban">{t('companyDetails.paymentSettings.iban')}</Label>
                  <Input id="iban" {...register("iban")} placeholder={t('companyDetails.paymentSettings.ibanPlaceholder')} />
                </div>

                <div className="col-span-1">
                  <Label htmlFor="bic">{t('companyDetails.paymentSettings.bic')}</Label>
                  <Input id="bic" {...register("bic")} placeholder={t('companyDetails.paymentSettings.bicPlaceholder')} />
                </div>

                <div className="col-span-1">
                  <Label htmlFor="bankName">{t('companyDetails.paymentSettings.bankName')}</Label>
                  <Input id="bankName" {...register("bankName")} placeholder={t('companyDetails.paymentSettings.bankNamePlaceholder')} />
                </div>
              </div>
            </div>
          </TabsContent>
          
          {/* PDF Settings Tab */}
          <TabsContent value="pdf" className="mt-6">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t('companyDetails.pdfSettings.title')}</CardTitle>
                  <CardDescription>{t('companyDetails.pdfSettings.description')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <Label className="text-base">
                        {t('companyDetails.pdfSettings.showDebugGrid')}
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        {t('companyDetails.pdfSettings.showDebugGridDescription')}
                      </p>
                    </div>
                    <Switch
                      checked={pdfShowDebugGrid}
                      onCheckedChange={(checked) => setValue("pdfShowDebugGrid", checked)}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </form>
    </div>
  );
}

